import {
  ChevronUpDownIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  ArrowPathIcon,
  NoSymbolIcon,
  PencilIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { SortData } from "./SortData";
import ActionPopup from "./ActionPopup";
import Pagination from "./Pagination";
import DeletePopup from "./DeletePopup";
import MasterTitle from "./MasterTitle";
import Model from "./Model";
import { Button } from "./Button";
import { Input } from "./Input";
import { Api } from "../Api/Api";
import { ErrorToast, SuccessToast } from "./toast";
import NoDataFound from "./NoDataFound";
import ActionButton from "./ActionButton";
import { TrashIcon } from "@heroicons/react/20/solid";
import Records from "./Records";
import { useDebounce } from "../hooks/use-debounce";
import { getDecryptedDataFromLocalStorage } from "../Api/encryption";

const ApiKey = (props) => {
  const accessRole = props?.access?.find((e) => e?.name === "Access");
  const adminUserData = getDecryptedDataFromLocalStorage("userData");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectId, setSelectId] = useState();
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [dataTable, setDataTable] = useState([]);
  const [isModel, setIsModel] = useState(false);
  const [error, setError] = useState({});
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [openRevokeModel, setOpenRevokeModel] = useState(false);
  const [openRotateModel, setOpenRotateModel] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 800);
  const [isLoader, setIsLoader] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [newKeyData, setNewKeyData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
  });

  const [titleData, setTitleData] = useState([
    { title: "NAME", sort: 1, fieldName: "name" },
    { title: "KEY PREFIX", sort: 1, fieldName: "key_prefix" },
    { title: "STATUS", sort: 1, fieldName: "is_active" },
    { title: "CREATED AT", sort: 1, fieldName: "createdAt" },
    { title: "LAST USED", sort: 1, fieldName: "last_used_at" },
    { title: "ACTIONS", sort: 0, fieldName: "" },
  ]);

  const handleChange = (name, value) => {
    setFormData((oldValue) => ({
      ...oldValue,
      [name]: value,
    }));
  };

  const validation = () => {
    let errors = {};
    let formIsValid = true;
    if (!formData?.name) {
      formIsValid = false;
      errors["name"] = "Please Enter Name.";
    }
    setError(errors);
    return formIsValid;
  };

  const _getData = async (limit, offset, search) => {
    try {
      setIsLoader(true);
      const { data } = await Api.getRequest(
        `api/admin/api-keys?limit=${limit}&offset=${offset}&search=${search || ""}`
      );
      const req = JSON.parse(data);
      if (req?.status === 200) {
        setDataTable(req?.data || []);
        setTotalCount(req?.totalCount || 0);
      }
      setIsLoader(false);
    } catch (error) {
      ErrorToast(error?.message);
      setIsLoader(false);
    }
  };

  const _addKey = async () => {
    if (validation()) {
      setIsLoading(true);
      try {
        let res;
        const payload = {
          name: formData.name,
          expires_at: formData.expires_at || null,
          rate_limit_per_min: formData.rate_limit_per_min || null,
          notes: formData.notes || null,
          scopes: [
            "boards.read",
            "mediums.read",
            "standards.read",
            "subjects.read",
            "chapters.read",
            "questions.read",
            "questions.sample",
            "questions.grade",
          ],
        };
        if (editData) {
          res = await Api.patchRequest(`api/admin/api-keys/${editData.id}`, payload);
        } else {
          res = await Api.postRequest("api/admin/api-keys", payload);
        }
        const req = JSON.parse(res.data);
        if (req?.status === 200) {
          SuccessToast(req?.message);
          if (req.apiKey) {
             setNewKeyData(req.apiKey); // Store the actual key to show the user once
          } else {
             setIsModel(false);
             _getData(recordsPerPage, currentPage, search);
          }
          setFormData({ name: "" });
          setEditData(null);
        } else {
          ErrorToast(req?.message);
        }
      } catch (error) {
        ErrorToast(error?.message);
      }
      setIsLoading(false);
    }
  };

  const _revokeKey = async () => {
    try {
      const { data } = await Api.postRequest(`api/admin/api-keys/${selectId}/revoke`);
      const req = JSON.parse(data);
      if (req?.status === 200) {
        SuccessToast(req?.message);
        _getData(recordsPerPage, currentPage, search);
        setOpenRevokeModel(false);
      } else {
        ErrorToast(req?.message);
      }
    } catch (error) {
      ErrorToast(error?.message);
    }
  };

  const _rotateKey = async () => {
    try {
      const { data } = await Api.postRequest(`api/admin/api-keys/${selectId}/rotate`);
      const req = JSON.parse(data);
      if (req?.status === 200) {
        SuccessToast(req?.message);
        setNewKeyData(req.apiKey);
        setOpenRotateModel(false);
        _getData(recordsPerPage, currentPage, search);
      } else {
        ErrorToast(req?.message);
      }
    } catch (error) {
      ErrorToast(error?.message);
    }
  };

  useEffect(() => {
    _getData(recordsPerPage, currentPage, search);
  }, [recordsPerPage, currentPage, debouncedValue]);

  const npage = Math.ceil(totalCount / recordsPerPage);
  const numbers = [...Array(npage + 1).keys()].slice(1);
  const firstIndex = (currentPage - 1) * recordsPerPage;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    SuccessToast("Copied to clipboard!");
  };

  return (
    <div className="h-full flex items-center justify-center ">
      <div className="h-full flex-1 flex flex-col space-y-5 lg:space-y-0 lg:flex-row lg:space-x-10 w-full sm:px-2">
        <div className="flex-1 px-2 sm:px-0 mt-2 w-full">
          <div className="flex justify-between items-center">
            <div>
              <MasterTitle
                title="API Keys"
                description="Manage your API keys for third-party integrations."
              />
            </div>
            <div className="flex items-center space-x-2">
              {accessRole?.add && (
                <Button
                  id=""
                  type="submit"
                  icon={PlusIcon}
                  label={"ADD API KEY"}
                  onClick={() => {
                    setEditData(null);
                    setNewKeyData(null);
                    setFormData({ name: "" });
                    setIsModel(true);
                  }}
                />
              )}
            </div>
          </div>

          <div className="mt-5 bg-white shadow-md rounded-xl p-4">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
              <div className="relative w-full sm:w-64">
                <Input
                  id="search"
                  name="search"
                  type="text"
                  placeholder="Search here..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Records
                  recordsPerPage={recordsPerPage}
                  setRecordsPerPage={setRecordsPerPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    {titleData.map((title, index) => (
                      <th
                        key={index}
                        className="p-3 text-subtitle font-roboto font-semibold text-xs uppercase"
                      >
                        <div className="flex items-center space-x-1">
                          <span>{title.title}</span>
                          {title.sort === 1 && (
                            <ChevronUpDownIcon
                              className="w-4 h-4 cursor-pointer"
                              onClick={() => {
                                const sortedData = SortData(
                                  dataTable,
                                  title.fieldName,
                                  title.sort
                                );
                                setDataTable([...sortedData]);
                                const newTitleData = [...titleData];
                                newTitleData[index].sort = title.sort === 1 ? 2 : 1;
                                setTitleData(newTitleData);
                              }}
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataTable.length > 0 ? (
                    dataTable.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-sm text-gray-700">{item.name}</td>
                        <td className="p-3 text-sm text-gray-700 font-mono">
                          <div className="flex items-center space-x-2">
                            <span>{item.key_prefix}...</span>
                            <DocumentDuplicateIcon
                              className="w-4 h-4 cursor-pointer text-gray-400 hover:text-blue-600"
                              onClick={() => copyToClipboard(item.key_prefix)}
                              title="Copy Prefix"
                            />
                          </div>
                        </td>
                        <td className="p-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.is_active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-500">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-sm text-gray-500">
                          {item.last_used_at ? new Date(item.last_used_at).toLocaleString() : "Never"}
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex items-center space-x-3">
                            {accessRole?.edit && (
                              <button
                                onClick={() => {
                                  setEditData(item);
                                  setFormData({
                                    name: item.name,
                                    expires_at: item.expires_at ? item.expires_at.split("T")[0] : "",
                                    rate_limit_per_min: item.rate_limit_per_min || "",
                                    notes: item.notes || "",
                                  });
                                  setIsModel(true);
                                }}
                                title="Edit"
                                className="text-gray-500 hover:text-blue-600 transition-colors"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                            )}
                            {accessRole?.edit && item.is_active && (
                              <button
                                onClick={() => {
                                  setSelectId(item.id);
                                  setOpenRevokeModel(true);
                                }}
                                title="Revoke"
                                className="text-gray-500 hover:text-orange-600 transition-colors"
                              >
                                <NoSymbolIcon className="w-5 h-5" />
                              </button>
                            )}
                            {accessRole?.edit && (
                              <button
                                onClick={() => {
                                  setSelectId(item.id);
                                  setOpenRotateModel(true);
                                }}
                                title="Rotate"
                                className="text-gray-500 hover:text-green-600 transition-colors"
                              >
                                <ArrowPathIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={titleData.length} className="p-5">
                        <NoDataFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                recordsPerPage={recordsPerPage}
                data={dataTable}
                dataCount={totalCount}
                numbers={numbers}
                npage={npage}
                firstIndex={firstIndex}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Model
        title={editData ? "Edit API Key" : "Add API Key"}
        show={isModel}
        onCancel={() => {
          setIsModel(false);
          setNewKeyData(null);
        }}
        onClose={() => {
          setIsModel(false);
          setNewKeyData(null);
        }}
        onOk={newKeyData ? () => {
          setIsModel(false);
          setNewKeyData(null);
          _getData(recordsPerPage, currentPage, search);
        } : _addKey}
        okText={newKeyData ? "I HAVE COPIED IT" : (editData ? "UPDATE" : "GENERATE KEY")}
        isLoading={isLoading}
      >
        {!newKeyData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="e.g. My Website Integration"
                />
                {error.name && <p className="text-red-500 text-xs mt-1">{error.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                <Input
                  name="expires_at"
                  type="date"
                  value={formData.expires_at || ""}
                  onChange={(e) => handleChange("expires_at", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (req/min)</label>
                <Input
                  name="rate_limit_per_min"
                  type="number"
                  value={formData.rate_limit_per_min || ""}
                  onChange={(e) => handleChange("rate_limit_per_min", e.target.value)}
                  placeholder="Leave empty for no limit"
                />
              </div>
              {editData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Prefix</label>
                  <div className="p-2 bg-gray-50 rounded-md border border-gray-200 text-sm font-mono text-gray-600 flex justify-between items-center">
                    <span>{editData.key_prefix}...</span>
                    <DocumentDuplicateIcon
                      className="w-4 h-4 cursor-pointer text-gray-400 hover:text-blue-600"
                      onClick={() => copyToClipboard(editData.key_prefix)}
                      title="Copy Prefix"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows="3"
                value={formData.notes || ""}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Internal notes about what this key is used for..."
              ></textarea>
            </div>
          </div>
        ) : (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800 font-medium">
              Your API key has been generated. Please copy it now as you won't be able to see it again.
            </p>
            <div className="bg-white p-3 rounded border border-blue-200 break-all font-mono text-sm select-all">
              {newKeyData}
            </div>
          </div>
        )}
      </Model>

      {/* Revoke Confirmation */}
      <DeletePopup
        show={openRevokeModel}
        title="Revoke API Key"
        description="Are you sure you want to revoke this API key? This action cannot be undone and any service using this key will immediately lose access."
        deleteText="Revoke"
        onCancel={() => setOpenRevokeModel(false)}
        onDelete={_revokeKey}
      />

      {/* Rotate Confirmation */}
      <DeletePopup
        show={openRotateModel}
        title="Rotate API Key"
        description="Rotating this key will generate a new secret and invalidate the current one. Any service using the old secret will lose access."
        deleteText="Rotate"
        onCancel={() => setOpenRotateModel(false)}
        onDelete={_rotateKey}
      />
    </div>
  );
};

export default ApiKey;
